// Pure, framework-free logic for the Manage Subscription screen. Mirrors the
// iOS `ManageSubscriptionStateMapper` / Android `ManageSubscriptionStateMapper`
// so web renders the same product names, pill states, dates and payloads.
// Everything here is deterministic and unit-tested in
// __tests__/subscriptionLogic.test.ts.

export type PillState = "active" | "cancelled" | "paused";
export type SubscriptionSource = "stripe" | "appstore" | "playstore" | "other";
export type PauseDurationEnum = "ONE_MONTH" | "THREE_MONTHS" | "SIX_MONTHS";

export type CancelReasonSlug =
  | "too_expensive"
  | "dont_use_enough"
  | "switching_apps"
  | "missing_features"
  | "not_working"
  | "other";

export function productName(productIdentifier: string | null | undefined): string {
  const id = (productIdentifier || "").toLowerCase();
  if (id.includes("annual") || id.includes("yearly") || id.includes("year")) {
    return "PRO+ Annual subscription";
  }
  if (id.includes("monthly") || id.includes("month")) {
    return "PRO+ Monthly subscription";
  }
  if (id.includes("lifetime")) {
    return "PRO+ Lifetime subscription";
  }
  return "PRO+ subscription";
}

export function pillState(
  entitlement: { willRenew?: boolean | null; expirationDate?: string | Date | null },
  isPaused = false
): PillState {
  if (isPaused) return "paused";
  // Lifetime (no expiry) and auto-renewing subs are "active"; a sub with an
  // expiry that won't renew is on its way out — "cancelled".
  if (!entitlement.expirationDate) return "active";
  return entitlement.willRenew ? "active" : "cancelled";
}

export function renewsOnLabel(state: PillState): string {
  switch (state) {
    case "cancelled":
      return "Ends on";
    case "paused":
      return "Resumes on";
    default:
      return "Renews on";
  }
}

export function subscriptionSource(store: string | null | undefined): SubscriptionSource {
  switch ((store || "").toLowerCase()) {
    case "stripe":
      return "stripe";
    case "app_store":
    case "appstore":
    case "mac_app_store":
      return "appstore";
    case "play_store":
    case "playstore":
      return "playstore";
    default:
      return "other";
  }
}

// Pause is only wired end-to-end against the Stripe backend mutation — neither
// App Store nor Play Store exposes a pause primitive we can drive from the web.
// Only an *active* sub can be paused: a paused one has nothing to pause, and a
// cancelled-but-not-yet-expired one would pause past its own end date (Stripe
// happily accepts pause_collection on a cancel_at_period_end sub).
export function hidesPauseAction(source: SubscriptionSource, state: PillState): boolean {
  return source !== "stripe" || state !== "active";
}

export type CancelStep = "store" | "lose" | "renewal" | "reason" | "pause" | "final";

// The cancel flow's step sequence per billing source. Stripe subs get the full
// retention flow ending in a real cancellation. Store-billed subs (and sources
// with no self-serve page at all) can't be cancelled by HiiKER, so they're told
// that on the FIRST screen — the store hand-off — and the retention content
// after it is optional. No "what you'll lose" and no final confirmation for
// them: we write nothing, so a red confirm button would only obstruct.
export function cancelFlowSteps(source: SubscriptionSource): CancelStep[] {
  if (source === "stripe") return ["lose", "renewal", "reason", "pause", "final"];
  return ["store", "renewal", "reason"];
}

export const APP_STORE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions";
export const PLAY_STORE_SUBSCRIPTIONS_URL = "https://play.google.com/store/account/subscriptions";

// Where "Go" sends a store-billed subscriber to manage payment/subscription.
// Prefers RevenueCat's managementURL (CustomerInfo exposes a single nullable
// URL, not one per store) and falls back to the well-known store pages.
export function storeManagementUrl(
  source: SubscriptionSource,
  managementURL: string | null | undefined
): string | null {
  switch (source) {
    case "appstore":
      return managementURL || APP_STORE_SUBSCRIPTIONS_URL;
    case "playstore":
      return managementURL || PLAY_STORE_SUBSCRIPTIONS_URL;
    default:
      return null;
  }
}

export function storeLabelFor(source: SubscriptionSource): string {
  switch (source) {
    case "appstore":
      return "App Store";
    case "playstore":
      return "Play Store";
    default:
      return "store";
  }
}

// How to route "update payment" / "cancel" for a given source. `other` covers
// RevenueCat sources we can't deep-link (promotional grants, Amazon, …): those
// have no self-serve page at all, so they get a support hand-off rather than a
// store redirect whose button would have nothing to open.
export type ManageAction =
  | { kind: "stripe" }
  | { kind: "store"; url: string; storeLabel: string }
  | { kind: "support" };

export function manageAction(
  source: SubscriptionSource,
  managementURL: string | null | undefined
): ManageAction {
  if (source === "stripe") return { kind: "stripe" };
  const url = storeManagementUrl(source, managementURL);
  return url ? { kind: "store", url, storeLabel: storeLabelFor(source) } : { kind: "support" };
}

export function updatePaymentPrompt(action: ManageAction): string {
  return action.kind === "store"
    ? `To update your payment details go to the ${action.storeLabel}`
    : "This subscription wasn't purchased through the web, App Store or Play Store. Contact support and we'll sort it out.";
}

// Copy for the store hand-off screen that opens a non-Stripe cancel flow. The
// CTA is named per the actual store ("Go to Play Store"), never generically.
export interface StoreCancelCopy {
  title: string;
  body: string;
  cta: string;
}

export function storeCancelCopy(action: ManageAction): StoreCancelCopy {
  if (action.kind === "store") {
    return {
      title: `Cancel through the ${action.storeLabel}`,
      body:
        `Your subscription was purchased through the ${action.storeLabel}, so HiiKER can't cancel it ` +
        `for you — it has to be cancelled in the ${action.storeLabel} directly. We'll take you there. ` +
        `Your PRO+ access continues until the end of the current billing period.`,
      cta: `Go to ${action.storeLabel}`,
    };
  }
  return {
    title: "We'll cancel it for you",
    body:
      "This subscription wasn't purchased through the web, App Store or Play Store. " +
      "Contact support and we'll cancel it for you.",
    cta: "Contact support",
  };
}

const ORDINAL_SUFFIX = (day: number): string => {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// "July 26th 2025"
export function longOrdinalDate(value: string | Date | null | undefined): string | null {
  const date = toDate(value);
  if (!date) return null;
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  return `${month} ${day}${ORDINAL_SUFFIX(day)} ${date.getFullYear()}`;
}

// "Aug 13, 2026"
export function shortDate(value: string | Date | null | undefined): string | null {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Reminder shown one week before renewal (expiry − 7 days).
export function reminderDate(expiry: string | Date | null | undefined): Date | null {
  const date = toDate(expiry);
  if (!date) return null;
  const result = new Date(date.getTime());
  result.setDate(result.getDate() - 7);
  return result;
}

// Resume date for a pause of N months from a given "now", clamped to the end of
// the target month. Bare setMonth overflows — Jan 31 + 1 month lands on Mar 3,
// not Feb 28 — which would misreport the resume date in the pause preview.
export function resumeDate(now: Date, months: number): Date {
  const result = new Date(now.getTime());
  const dayOfMonth = result.getDate();
  // Move to the 1st first so the month arithmetic can't roll over on its own.
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(dayOfMonth, daysInTargetMonth));
  return result;
}

// Backend `reason` for CancelWebSubscription: the analytics slug, or for "other"
// with free text `other: <text>` (just `other` when the box is empty/absent).
export function cancelReasonPayload(
  slug: CancelReasonSlug | null,
  otherText = ""
): string | null {
  if (!slug) return null;
  if (slug === "other") {
    const trimmed = otherText.trim();
    return trimmed ? `other: ${trimmed}` : "other";
  }
  return slug;
}

export function pauseDurationEnum(months: number): PauseDurationEnum {
  switch (months) {
    case 3:
      return "THREE_MONTHS";
    case 6:
      return "SIX_MONTHS";
    default:
      return "ONE_MONTH";
  }
}

export interface Entitlement {
  productIdentifier?: string | null;
  store?: string | null;
  willRenew?: boolean | null;
  originalPurchaseDate?: string | Date | null;
  expirationDate?: string | Date | null;
}

export interface DisplayModel {
  productName: string;
  pillState: PillState;
  priceString: string;
  startedOn: string | null;
  renewsOn: string | null;
  renewsOnLabel: string;
  source: SubscriptionSource;
  hidesPauseAction: boolean;
}

export function buildDisplayModel(
  entitlement: Entitlement,
  opts: { isPaused?: boolean; pauseResumesAt?: string | Date | null; priceString?: string | null } = {}
): DisplayModel {
  const isPaused = opts.isPaused ?? false;
  const source = subscriptionSource(entitlement.store);
  const state = pillState(entitlement, isPaused);
  // When paused, prefer the backend's pauseResumesAt over the entitlement's
  // expirationDate (the latter is the natural renewal, a year out for an annual
  // sub — not when the pause lifts). Fall back to expiry if it's absent.
  const renewsSource =
    state === "paused" ? opts.pauseResumesAt ?? entitlement.expirationDate : entitlement.expirationDate;
  return {
    productName: productName(entitlement.productIdentifier),
    pillState: state,
    priceString: opts.priceString || "—",
    startedOn: longOrdinalDate(entitlement.originalPurchaseDate),
    renewsOn: longOrdinalDate(renewsSource),
    renewsOnLabel: renewsOnLabel(state),
    source,
    hidesPauseAction: hidesPauseAction(source, state),
  };
}

// --- Loading helpers -------------------------------------------------------
// The RevenueCat SDK hands back Date objects while our GraphQL layer speaks ISO
// strings; normalise to ISO so everything downstream sees one shape.
export function toIso(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return String(value);
}

export function normalizeEntitlement(raw: any, expirationDate: string | null): Entitlement {
  return {
    productIdentifier: raw?.productIdentifier ?? null,
    store: raw?.store ?? null,
    willRenew: raw?.willRenew ?? null,
    originalPurchaseDate: toIso(raw?.originalPurchaseDate),
    expirationDate,
  };
}

// Price and pause state are fetched with Promise.allSettled so one failing
// query can't blank the whole screen — each degrades to its own safe default.
export function priceStringFromResult(result: PromiseSettledResult<any> | null | undefined): string | null {
  if (result?.status !== "fulfilled") return null;
  return result.value?.activeWebSubscriptionPrice?.formattedPrice ?? null;
}

export interface PauseState {
  isPaused: boolean;
  pauseResumesAt: string | null;
}

export function pauseStateFromResult(result: PromiseSettledResult<any> | null | undefined): PauseState {
  if (result?.status !== "fulfilled") return { isPaused: false, pauseResumesAt: null };
  const user = result.value?.user;
  return {
    isPaused: user?.isPaused ?? false,
    pauseResumesAt: toIso(user?.pauseResumesAt),
  };
}

export interface CancelReasonOption {
  slug: CancelReasonSlug;
  label: string;
}

export const CANCEL_REASONS: CancelReasonOption[] = [
  { slug: "too_expensive", label: "It's too expensive" },
  { slug: "dont_use_enough", label: "I don't use it enough" },
  { slug: "switching_apps", label: "I'm switching to another app" },
  { slug: "missing_features", label: "Missing features I need" },
  { slug: "not_working", label: "It's not working" },
  { slug: "other", label: "Other" },
];

export interface PauseDurationOption {
  months: number;
  label: string;
}

export const PAUSE_DURATIONS: PauseDurationOption[] = [
  { months: 1, label: "1 Month" },
  { months: 3, label: "3 Months" },
  { months: 6, label: "6 Months" },
];

export const SUBSCRIPTION_LOAD_FAILURE_MESSAGE =
  "We couldn't load your subscription details just now. Please refresh the page or try " +
  "again shortly — this doesn't affect your subscription or your billing.";

export type SubscriptionPanel = "loading" | "error" | "empty" | "manage";

/**
 * Which panel the subscription page shows.
 *
 * Extracted because the page had no error state at all: any failure loading the
 * entitlement fell through to `!display` and rendered the empty state, telling a
 * paying subscriber "You don't have an active subscription!" — the most alarming
 * possible reading of what is usually a transient network blip, on the one screen
 * where someone goes to check they still have what they paid for.
 *
 * `error` deliberately outranks `empty`: when we could not load the subscription
 * we do not know whether one exists, and saying nothing is better than guessing
 * wrong in the direction that panics people.
 */
export function subscriptionPanel(state: {
  loading: boolean;
  loadFailed: boolean;
  hasSubscription: boolean;
}): SubscriptionPanel {
  if (state.loading) return "loading";
  if (state.loadFailed) return "error";
  return state.hasSubscription ? "manage" : "empty";
}
